import React from 'react';

import {DropMenuReact, PositionParent} from 'common/components/drop-menu';
import {Directions} from 'common/components/drop-menu/types';
import {SvgIconReact} from 'common/components/svg-icon';
import {TooltipReact} from 'common/components/tooltip';
import {IconSizeType} from 'common/constants';
import {useTranslation} from 'common/i18n/hooks';
import {getProfilePath} from 'common/report/util/profile/utils';
import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerSize} from 'common/components/spinner/types';

import {TrackedUserIconReact} from '../tracked-user-icon';
import {RecordingMetadata, RecordingPageViewEventMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

type ReplayPlaylistRowContextMenuProps = {
  contextPanelOpen: boolean;
  sortedReplays: RecordingMetadata[];
  currentRecordingIdx: number;
  replay: RecordingMetadata;
  replayPageViewEventsLoaded: boolean;
  replayPageViewEvents: RecordingPageViewEventMetadata[];
  displayName: string;
  positionParent: PositionParent;
  onContextMenuHover: (currentIdx: number | null) => void;
};

function replayPlaylistRowContextMenu(props: ReplayPlaylistRowContextMenuProps) {
  const {t} = useTranslation();

  const isReplaySelected = props.replay.replayId === props.sortedReplays[props.currentRecordingIdx]?.replayId;

  const getOSLogoToShow = () => {
    switch (props.replay.os) {
      case `Mac OS X`:
      case `iOS`:
        return `apple-logo`;
      case `Windows`:
      case `Windows Phone`:
        return `windows-logo`;
      case `Ubuntu`:
        return `ubuntu-logo`;
      case `Android`:
        return `android-logo`;
      default:
        return `os`;
    }
  };

  const getBrowserLogoToShow = () => {
    const browserLowerCase = props.replay.browser?.toLowerCase() || ``;

    if (browserLowerCase.includes(`chrome`)) {
      return `chrome-logo`;
    } else if (browserLowerCase.includes(`firefox`)) {
      return `firefox-logo`;
    } else if (browserLowerCase.includes(`safari`)) {
      return `safari-logo`;
    } else if (browserLowerCase.includes(`edge`)) {
      return `edge-logo`;
    } else if (browserLowerCase.includes(`opera`)) {
      return `opera-logo`;
    } else if (browserLowerCase.includes(`brave`)) {
      return `brave-logo`;
    }
    return `browser`;
  };

  const startingURL = props.replay.startingURL || t(`session-replay.unknown-starting-url`);
  const exitURL =
    props.replayPageViewEvents.length > 0
      ? [...props.replayPageViewEvents].sort((a, b) => a.eventTime - b.eventTime)[props.replayPageViewEvents.length - 1]
          .currentURL
      : t(`session-replay.unknown-exit-url`);
  const currentIdx = props.sortedReplays.findIndex((replay) => replay.replayId === props.replay.replayId);

  return (
    <div
      className={styles.replaysListItemContextMenuContainer}
      onMouseOver={() => props.onContextMenuHover(currentIdx)}
      onMouseOut={() => props.onContextMenuHover(null)}
    >
      <DropMenuReact
        open={props.contextPanelOpen}
        offset={0}
        direction={Directions.Right}
        positionParent={props.positionParent}
      >
        <div className={styles.replaysListItemContextMenu}>
          <div className={styles.replaysListItemContext}>
            <TrackedUserIconReact
              className={styles.replaysListItemUserIcon}
              distinctId={props.replay.distinctId}
              imgSrc={props.replay.avatarUrl}
              email={props.replay.email}
              size={IconSizeType.Medium}
              enableOutline={isReplaySelected}
            />
            <div className={styles.replaysListItemText}>
              <div className={styles.replaysListItemTextTitle}>{props.displayName}</div>
              <div className={styles.replaysListItemTextSubtitle}>{props.replay.email}</div>
            </div>
            <a href={getProfilePath(props.replay.distinctId, null)} target='_blank'>
              <SvgIconReact className={styles.replaysListItemIcon} icon={`link-out`} />
            </a>
          </div>
          <div className={styles.replaysListItemContextIcons}>
            <div className={styles.replaysListItemContextIconContainer}>
              <SvgIconReact
                className={styles.replaysListItemContextIcon}
                icon={props.replay.source === `web` ? `laptop` : `mobile`}
              />
              <TooltipReact text={`Source SDK: ${props.replay.source || t(`session-replay.unknown-source`)}`} />
            </div>
            <div className={styles.replaysListItemContextIconContainer}>
              <SvgIconReact className={styles.replaysListItemContextIcon} icon={getOSLogoToShow()} />
              <TooltipReact text={`Operating System: ${props.replay.os || t(`session-replay.unknown-os`)}`} />
            </div>
            {props.replay.source === `web` && (
              <div className={styles.replaysListItemContextIconContainer}>
                <SvgIconReact className={styles.replaysListItemContextIcon} icon={getBrowserLogoToShow()} />
                <TooltipReact text={`Browser: ${props.replay.browser || t(`session-replay.unknown-browser`)}`} />
              </div>
            )}
          </div>
          {props.replay?.source === `web` && (
            <div className={styles.replaysListItemContextUrl}>
              <div className={styles.replaysListItemContextUrlText}>{t(`session-replay.starting-url`)}</div>
              <div className={styles.replaysListItemContextUrlText}>
                {startingURL}
                <TooltipReact text={startingURL} />
              </div>
            </div>
          )}
          {props.replay?.source === `web` && (
            <div className={styles.replaysListItemContextUrl}>
              <div className={styles.replaysListItemContextUrlText}>{t(`session-replay.exit-url`)}</div>
              {!props.replayPageViewEventsLoaded ? (
                <SpinnerReact className={styles.replaysListItemContextUrlSpinner} size={SpinnerSize.Small} />
              ) : (
                <div className={styles.replaysListItemContextUrlText}>
                  {exitURL}
                  <TooltipReact text={exitURL} />
                </div>
              )}
            </div>
          )}
        </div>
      </DropMenuReact>
    </div>
  );
}

export const ReplayPlaylistRowContextMenuReact = withStyles(React.forwardRef(replayPlaylistRowContextMenu), css);
export const ReplayPlaylistRowContextMenuEl = react2panel(`mp-replay-playlist-row`, ReplayPlaylistRowContextMenuReact);
